import { useEffect, useState } from "react";

/**
 * Light, dark, or whatever the system says.
 *
 * Three states, not two: an explicit choice is stamped on <html> as
 * `data-theme` and remembered in localStorage (index.html applies it before
 * React renders, so a dark choice never flashes light); no choice means the
 * system decides through `prefers-color-scheme`, which is how most people
 * will leave it. The stylesheet defines the dark tokens for both routes.
 */
export type ThemeChoice = "light" | "dark" | null;
export type Theme = "light" | "dark";

const KEY = "poolnerd-theme";
const EVENT = "poolnerd-theme";

export function storedTheme(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

export function systemTheme(): Theme {
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

export function effectiveTheme(choice: ThemeChoice = storedTheme()): Theme {
  return choice ?? systemTheme();
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice) root.setAttribute("data-theme", choice);
  else root.removeAttribute("data-theme");
}

export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice) localStorage.setItem(KEY, choice);
    else localStorage.removeItem(KEY);
  } catch {
    // Storage can be unavailable (private windows, strict settings). The
    // choice still applies for this page; it just will not be remembered.
  }
  applyTheme(choice);
  window.dispatchEvent(new CustomEvent(EVENT));
}

/** The current choice and what it resolves to, kept fresh across tabs and
 *  when the system theme changes underneath a "system" choice. */
export function useTheme(): { choice: ThemeChoice; effective: Theme; set: (choice: ThemeChoice) => void } {
  const [choice, setChoice] = useState<ThemeChoice>(() => storedTheme());
  const [system, setSystem] = useState<Theme>(() => systemTheme());

  useEffect(() => {
    const onChange = () => setChoice(storedTheme());
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onChange);
    let media: MediaQueryList | null = null;
    const onMedia = () => setSystem(systemTheme());
    if (typeof window.matchMedia === "function") {
      media = window.matchMedia("(prefers-color-scheme: dark)");
      media.addEventListener?.("change", onMedia);
    }
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onChange);
      media?.removeEventListener?.("change", onMedia);
    };
  }, []);

  return { choice, effective: choice ?? system, set: setTheme };
}
